// Purpose: admin break-glass forensics. Search by target, justification
// textarea (>=20 chars) + acknowledgment checkbox, Grant Access button.
// Active grants table with Terminate button. On grant, navigate to
// /admin/forensics/view with the ephemeral grant token.
//
// Phase 7.5 port: PageHeader + Card-wrapped request form (Input/Select/
// Textarea primitives) + DataTable active-grants list.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActiveGrantSummary, BreakGlassTargetItem } from '../../api/admin-forensics.ts';
import {
  grantForensicsAccess,
  listActiveGrants,
  listBreakGlassTargets,
  terminateGrant,
} from '../../api/admin-forensics.ts';
import { formatDateTime } from '../../lib/format.ts';
import {
  clearPendingForensicsGrant,
  consumePendingForensicsGrant,
  stashPendingForensicsGrant,
} from '../../lib/pendingActions.ts';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  Modal,
  SearchableSelect,
  Select,
  Textarea,
  type Column,
} from '../../components/ui/index.ts';

export function Forensics() {
  const nav = useNavigate();
  const [targetType, setTargetType] = useState<'share' | 'dropbox_upload'>('share');
  const [targetId, setTargetId] = useState('');
  const [justification, setJustification] = useState('');
  const [ack, setAck] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<ActiveGrantSummary[]>([]);
  const [terminateTarget, setTerminateTarget] = useState<ActiveGrantSummary | null>(null);
  const [terminateReason, setTerminateReason] = useState('');

  // Commit 8b.12: target picker state. Loaded once per targetType change;
  // refresh on tab focus / nav away → back picks up new shares too. The
  // SearchableSelect handles the manual-ID fallback for operators
  // inspecting archived targets that aren't in the active+recent list.
  const [targets, setTargets] = useState<BreakGlassTargetItem[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  const load = async (): Promise<void> => {
    try {
      setSessions(await listActiveGrants());
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTargetsLoading(true);
    setTargets([]);
    listBreakGlassTargets(targetType)
      .then((items) => {
        if (!cancelled) setTargets(items);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setTargetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetType]);

  // Commit 8b.12.1: shared submit + auto-continue. The Forensics page
  // POST hits 428 if MFA is stale; the axios interceptor redirects to
  // step-up; after MFA the SPA returns here. Without a stash the
  // operator would have had to re-fill the form. With the 8b.6-style
  // stash, the page mount calls submitGrant() with the stashed values
  // and the flow completes automatically.
  //
  // Commit 8b.12.3: clear the stash on SUCCESS before navigating away.
  // Without this, a future mount of /admin/forensics inside the 5-min
  // TTL (e.g. operator hits Back from the viewer) re-consumes the
  // leftover stash and fires another grant — which audited as multiple
  // admin.forensics.access_granted rows for one operator click. The
  // stash now exists strictly for the 428-bounce window: written
  // before the POST, cleared on the POST's success path or on
  // consume()'s read.
  const submitGrant = async (input: {
    targetType: 'share' | 'dropbox_upload';
    targetId: string;
    justification: string;
  }): Promise<void> => {
    setWorking(true);
    try {
      stashPendingForensicsGrant({
        ...input,
        acknowledged: true,
      });
      const result = await grantForensicsAccess(input);
      clearPendingForensicsGrant();
      nav(`/admin/forensics/view?grant=${encodeURIComponent(result.grantToken)}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onGrant = async (): Promise<void> => {
    setError(null);
    if (justification.trim().length < 20) {
      setError('Justification must be at least 20 characters.');
      return;
    }
    if (!ack) {
      setError('Acknowledge the access warning before proceeding.');
      return;
    }
    await submitGrant({
      targetType,
      targetId: targetId.trim(),
      justification: justification.trim(),
    });
  };

  // Auto-continue on mount: if the page was reached via the MFA
  // bounce-and-resume path, replay the grant once. Single-shot —
  // consumePendingForensicsGrant() removes the entry from sessionStorage
  // so a subsequent mount (e.g. the user nav'ing away and back) doesn't
  // re-fire. Server-side mfa middleware is the authoritative gate;
  // a stale stash that somehow survives the 5-min TTL just gets a
  // fresh 428 (and the interceptor + cleared-stash combo stops the
  // cycle there).
  //
  // Commit 8b.12.3: useRef sentinel guards against double-fire from
  // any source — StrictMode dev double-invoke, React Router transition
  // remount, future framework changes. consume() is destructive and
  // would normally be enough, but a fast double-mount that reads the
  // stash before either consume() commits its remove can race; the
  // sentinel is the deterministic guard.
  const autoContinueFiredRef = useRef(false);
  useEffect(() => {
    if (autoContinueFiredRef.current) return;
    const stash = consumePendingForensicsGrant();
    if (!stash) return;
    autoContinueFiredRef.current = true;
    // Hydrate the form so the operator can see what's about to fire.
    setTargetType(stash.targetType);
    setTargetId(stash.targetId);
    setJustification(stash.justification);
    setAck(stash.acknowledged);
    void submitGrant({
      targetType: stash.targetType,
      targetId: stash.targetId,
      justification: stash.justification,
    });
  }, []);

  const onTerminate = async (): Promise<void> => {
    if (!terminateTarget) return;
    if (terminateReason.trim().length < 10) {
      setError('Termination reason must be at least 10 characters.');
      return;
    }
    try {
      await terminateGrant(terminateTarget.accessId, terminateReason.trim());
      setTerminateTarget(null);
      setTerminateReason('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const columns: Column<ActiveGrantSummary>[] = [
    {
      key: 'admin',
      header: 'Admin',
      cell: (s) => <span className="text-body-s text-fg-primary">{s.adminEmail}</span>,
    },
    {
      key: 'target',
      header: 'Target',
      cell: (s) => (
        <span className="font-mono text-body-s text-fg-secondary">
          {s.targetType}:{s.targetId.slice(0, 8)}…
        </span>
      ),
    },
    {
      key: 'granted',
      header: 'Granted',
      hideOnMobile: true,
      cell: (s) => <span className="text-body-s text-fg-muted">{formatDateTime(s.grantedAt)}</span>,
    },
    {
      key: 'expires',
      header: 'Expires',
      hideOnMobile: true,
      cell: (s) => <span className="text-body-s text-fg-muted">{formatDateTime(s.expiresAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '140px',
      cell: (s) => (
        <Button variant="danger" size="sm" onClick={() => setTerminateTarget(s)}>
          Terminate
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Break-glass forensics"
        subtitle="Admin content inspection is an audit-everything operation. Justifications land in the audit log alongside an admin.content.accessed row; the grant expires automatically after the break-glass window."
      />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Request access</CardTitle>
        </CardHeader>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select
              label="Target type"
              value={targetType}
              onChange={(e) => {
                setTargetType(e.target.value as 'share' | 'dropbox_upload');
                setTargetId('');
              }}
            >
              <option value="share">share</option>
              <option value="dropbox_upload">dropbox_upload</option>
            </Select>
            {/* Commit 8b.12: SearchableSelect populated from
                /api/admin/break-glass/targets/{shares,dropbox-uploads}
                with server-side decrypted labels. The "Or enter ID
                manually →" fallback inside the dropdown switches to a
                free-text input for archived/deleted target IDs. */}
            <div className="flex flex-col gap-2">
              <label className="text-label uppercase text-fg-secondary">Target</label>
              <SearchableSelect
                options={targets.map((t) => ({
                  value: t.id,
                  label: `${t.label}${t.isActive ? '' : ' (inactive)'}`,
                  hint: formatDateTime(t.createdAt),
                }))}
                value={targetId}
                onChange={setTargetId}
                loading={targetsLoading}
                placeholder={targetType === 'share' ? 'Pick a share…' : 'Pick an upload…'}
                emptyText="No active or recent targets in the last 30 days."
                aria-label="Break-glass target"
              />
            </div>
          </div>
          <Textarea
            label="Justification (≥20 chars)"
            rows={4}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
          <label className="flex items-center gap-2 text-body-s text-fg-secondary">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            <span>
              I acknowledge this access is audited and will be visible to the engineering lead and
              compliance reviewers.
            </span>
          </label>
          <div className="flex justify-end">
            <Button
              variant="danger"
              size="md"
              loading={working}
              disabled={!targetId.trim()}
              onClick={() => void onGrant()}
            >
              Grant access
            </Button>
          </div>
        </div>
      </Card>

      <Card padding="none">
        <div className="px-5 py-3 border-b border-border-default">
          <CardTitle>
            Active grants <span className="text-body-s text-fg-muted">({sessions.length})</span>
          </CardTitle>
        </div>
        {sessions.length === 0 ? (
          <p className="p-5 text-body-s text-fg-muted">No active grants.</p>
        ) : (
          <DataTable<ActiveGrantSummary>
            columns={columns}
            rows={sessions}
            getRowKey={(s) => s.accessId}
            caption="Active forensics grants"
            className="border-0 rounded-none"
          />
        )}
      </Card>

      <Modal
        open={!!terminateTarget}
        onClose={() => setTerminateTarget(null)}
        title="Terminate forensics grant"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setTerminateTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={() => void onTerminate()}>
              Terminate
            </Button>
          </>
        }
      >
        <p>
          Force-close grant <code>{terminateTarget?.accessId.slice(0, 12)}…</code>. A close-event
          audit row is written with the supplied reason.
        </p>
        <Textarea
          className="mt-3"
          rows={3}
          value={terminateReason}
          onChange={(e) => setTerminateReason(e.target.value)}
          placeholder="Reason (≥10 chars)"
        />
      </Modal>
    </div>
  );
}
